import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, Power, Clock, Calendar, Plus, X, AlertCircle, AlertTriangle, Zap } from "lucide-react";
import Header from "../components/layout/Header";
import { Badge, Modal } from "../components/ui/index";
import { automatisationsAPI, noeudsAPI, projetsAPI, parametresAPI } from "../api/services";

const mois = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const moisKeys = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

const toMinutes = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; };

export default function CreerAutomatisation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const projetIdParam = searchParams.get("projet");
  const revendeurIdParam = searchParams.get("revendeur");
  const clientIdParam = searchParams.get("client");

  const jours = [
    t('createAutomation.days.mon').charAt(0).toUpperCase(),
    t('createAutomation.days.tue').charAt(0).toUpperCase(),
    t('createAutomation.days.wed').charAt(0).toUpperCase(),
    t('createAutomation.days.thu').charAt(0).toUpperCase(),
    t('createAutomation.days.fri').charAt(0).toUpperCase(),
    t('createAutomation.days.sat').charAt(0).toUpperCase(),
    t('createAutomation.days.sun').charAt(0).toUpperCase()
  ];
  const joursLabels = [
    t('createAutomation.days.mon'),
    t('createAutomation.days.tue'),
    t('createAutomation.days.wed'),
    t('createAutomation.days.thu'),
    t('createAutomation.days.fri'),
    t('createAutomation.days.sat'),
    t('createAutomation.days.sun')
  ];
  const moisLabels = moisKeys.map(k => t(`createAutomation.months.${k}`));

  const [nom, setNom] = useState("");
  const [description, setDescription] = useState("");
  const [selectedJours, setSelectedJours] = useState([]);
  const [selectedMois, setSelectedMois] = useState([]);
  const [creneaux, setCreneaux] = useState([]);
  // Tri stable des créneaux par heure de début (HH:MM), puis par heure de fin
  const sortCreneaux = (list) => [...list].sort((a, b) => {
    const cmp = (a.debut || '').localeCompare(b.debut || '');
    return cmp !== 0 ? cmp : (a.fin || '').localeCompare(b.fin || '');
  });
  const [heureDebut, setHeureDebut] = useState("08:00");
  const [heureFin, setHeureFin] = useState("18:00");
  const [action, setAction] = useState("Activer");
  const [creneauActionneurs, setCreneauActionneurs] = useState([]); // actionneurs ciblés pour le créneau en cours

  // Actionneurs
  const [actionneurs, setActionneurs] = useState([]); // available
  const [selectedActionneurs, setSelectedActionneurs] = useState([]); // selected IDs
  const [projet, setProjet] = useState(null);
  const [projetId, setProjetId] = useState(projetIdParam);
  const [revendeurId, setRevendeurId] = useState(revendeurIdParam);
  const [clientId, setClientId] = useState(clientIdParam);

  // Timezone
  const [timezone, setTimezone] = useState('Europe/Paris');

  // Conflits
  const [conflicts, setConflicts] = useState([]); // time conflicts (blocking) + cross conflicts
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");
  const [loadingEdit, setLoadingEdit] = useState(isEdit);

  // Plages occupées & modal conflits
  const [existingAutos, setExistingAutos] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState(null);

  // Charger les données existantes en mode édition
  useEffect(() => {
    if (!isEdit) return;
    const loadAutomatisation = async () => {
      try {
        const res = await automatisationsAPI.getOne(editId);
        const auto = res.data?.data || res.data;
        setNom(auto.nom || "");
        setDescription(auto.description || "");
        setSelectedJours(auto.selectedJours || auto.jours || []);
        setSelectedMois(auto.selectedMois || auto.mois || []);
        const autoActIds = (auto.actionneurs || []).map(a => typeof a === "string" ? a : a._id);
        setSelectedActionneurs(autoActIds);
        // Normalisation legacy : un créneau avec actionneurs:[] signifiait "tous les actionneurs
        // de l'auto". On le convertit en liste explicite pour que de futures modifs (ajout/retrait
        // d'actionneur sur l'auto) ne réétendent pas rétroactivement le créneau.
        const normalizedCreneaux = (auto.creneaux || []).map(c => {
          const acts = (c.actionneurs || []).map(a => typeof a === "string" ? a : a._id);
          return acts.length === 0
            ? { ...c, actionneurs: [...autoActIds] }
            : { ...c, actionneurs: acts };
        });
        setCreneaux(sortCreneaux(normalizedCreneaux));
        const pId = auto.projet?._id || auto.projet;
        setProjetId(pId);
        setRevendeurId(auto.revendeur?._id || auto.revendeur || revendeurIdParam);
        setClientId(auto.client?._id || auto.client || clientIdParam);
        if (auto.timezone) setTimezone(auto.timezone);
      } catch (err) {
        setApiError("Erreur lors du chargement de l'automatisation");
      } finally {
        setLoadingEdit(false);
      }
    };
    loadAutomatisation();
  }, [editId, isEdit]);

  // Charger le fuseau horaire depuis les paramètres
  useEffect(() => {
    parametresAPI.get().then(res => {
      const fuseau = res.data?.data?.preferences?.fuseau || 'Europe/Paris (GMT+1)';
      // Extraire le timezone IANA (avant le " (")
      const tz = fuseau.split(' (')[0] || 'Europe/Paris';
      setTimezone(tz);
    }).catch(() => {});
  }, []);

  // Load project info and actuators
  useEffect(() => {
    if (!projetId) return;
    projetsAPI.getOne(projetId).then(res => setProjet(res.data?.data || res.data)).catch(() => {});
    noeudsAPI.getAll({ projet: projetId, categorie: "Actionneur" })
      .then(res => setActionneurs(res.data?.data || res.data || []))
      .catch(() => {});
  }, [projetId]);

  // Charger les automatisations existantes du projet
  useEffect(() => {
    if (!projetId) return;
    automatisationsAPI.getAll({ projet: projetId })
      .then(res => {
        const all = res.data?.data || res.data || [];
        setExistingAutos(isEdit ? all.filter(a => (a._id || a.id) !== editId) : all);
      })
      .catch(() => setExistingAutos([]));
  }, [projetId, isEdit, editId]);

  // Calcul des plages occupées
  // Filtre = soit les actionneurs ciblés explicitement par le créneau en cours d'édition,
  // soit (par défaut) tous ceux sélectionnés au niveau de l'auto.
  const occupiedSlots = useMemo(() => {
    const effectiveTargets = creneauActionneurs.length > 0 ? creneauActionneurs : selectedActionneurs;
    if (!effectiveTargets.length || !selectedJours.length) return [];
    const slots = [];
    for (const auto of existingAutos) {
      const autoActIds = (auto.actionneurs || []).map(a => typeof a === "string" ? a : a._id);
      const hasCommonAct = autoActIds.some(id => effectiveTargets.includes(id));
      if (!hasCommonAct) continue;

      const autoJours = auto.selectedJours || auto.jours || [];
      const hasCommonJour = autoJours.some(j => selectedJours.includes(j));
      if (!hasCommonJour) continue;

      const autoMois = auto.selectedMois || auto.mois || [];
      if (selectedMois.length > 0 && autoMois.length > 0) {
        const hasCommonMois = autoMois.some(m => selectedMois.includes(m));
        if (!hasCommonMois) continue;
      }

      for (const c of (auto.creneaux || [])) {
        // Si le créneau cible des actionneurs spécifiques, ne garder que ceux en commun
        const creneauActIds = (c.actionneurs && c.actionneurs.length > 0)
          ? c.actionneurs.map(a => typeof a === "string" ? a : a._id)
          : autoActIds;
        const creneauCommon = creneauActIds.filter(id => effectiveTargets.includes(id));
        if (creneauCommon.length === 0) continue;

        const creneauActNames = creneauCommon.map(id => {
          const act = (auto.actionneurs || []).find(a => (typeof a === "string" ? a : a._id) === id);
          return act?.sn || id.slice(-6);
        });

        slots.push({ debut: c.debut, fin: c.fin, action: c.action, autoNom: auto.nom || "Sans nom", actionneurs: creneauActNames });
      }
    }
    return slots;
  }, [existingAutos, selectedActionneurs, creneauActionneurs, selectedJours, selectedMois]);

  const toggleJour = (i) => setSelectedJours(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  const toggleMois = (m) => setSelectedMois(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);

  // Convertit un créneau (debut, fin) en plage(s) [start, end) — supporte la traversée de minuit
  const toRanges = (debut, fin) => {
    const s = toMinutes(debut);
    const e = toMinutes(fin);
    return e > s ? [[s, e]] : [[s, 1440], [0, e]];
  };
  const rangesOverlap = (a, b) =>
    a.some(([a0, a1]) => b.some(([b0, b1]) => a0 < b1 && b0 < a1));

  const addCreneau = () => {
    if (selectedActionneurs.length === 0) return;
    if (heureDebut === heureFin) return; // créneau vide refusé ; debut > fin = passage minuit autorisé

    // Snapshot : les actionneurs ciblés au moment de la création du créneau
    // - si l'utilisateur a ciblé explicitement → on prend cette liste
    // - sinon on snapshot la sélection actuelle de l'auto (option A)
    const targetActuators = creneauActionneurs.length > 0
      ? [...creneauActionneurs]
      : [...selectedActionneurs];

    const newRanges = toRanges(heureDebut, heureFin);

    // Conflit uniquement si action opposée (Activer vs Désactiver) — sinon redondance silencieuse
    const overlap = occupiedSlots.find(s => {
      if (s.action === action) return false;
      return rangesOverlap(newRanges, toRanges(s.debut, s.fin));
    });
    if (overlap) {
      setOverlapWarning({ debut: heureDebut, fin: heureFin, action, overlap, actionneurs: targetActuators });
      setShowConflictModal(true);
      return;
    }
    setCreneaux(prev => sortCreneaux([...prev, { debut: heureDebut, fin: heureFin, action, actionneurs: targetActuators }]));
    setCreneauActionneurs([]);
  };

  const addActionneur = (id) => {
    if (!id || selectedActionneurs.includes(id)) return;
    setSelectedActionneurs(prev => [...prev, id]);
  };

  const removeActionneur = (id) => {
    setSelectedActionneurs(prev => prev.filter(x => x !== id));
    // Retirer cet actionneur de chaque créneau qui le cible, puis purger
    // les créneaux devenus vides (orphelins ne pilotant plus rien).
    setCreneaux(prev => prev
      .map(c => ({
        ...c,
        actionneurs: (c.actionneurs || []).filter(aid => aid !== id),
      }))
      .filter(c => (c.actionneurs || []).length > 0)
    );
  };

  // Validation locale
  const errors = [];
  if (!nom) errors.push(t('createAutomation.nameRequired'));
  if (creneaux.length === 0) errors.push(t('createAutomation.slotRequired'));
  if (selectedJours.length === 0) errors.push(t('createAutomation.dayRequired'));
  if (selectedActionneurs.length === 0) errors.push(t('createAutomation.actuatorRequired'));
  if (!projetId) errors.push(t('createAutomation.noProject'));
  const isValid = errors.length === 0;

  // Vérification des conflits en temps réel quand les paramètres changent
  useEffect(() => {
    if (!selectedActionneurs.length || !selectedJours.length || !creneaux.length) {
      setConflicts([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await automatisationsAPI.checkConflicts({
          actionneurs: selectedActionneurs,
          selectedJours,
          selectedMois,
          creneaux,
          projet: projetId,
          ...(isEdit && { excludeId: editId })
        });
        setConflicts(res.data?.data?.conflicts || []);
      } catch {
        setConflicts([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [selectedActionneurs, selectedJours, selectedMois, creneaux]);

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    setApiError("");
    const payload = {
      nom,
      description,
      projet: projetId,
      revendeur: revendeurId || null,
      client: clientId || null,
      selectedJours,
      selectedMois,
      creneaux,
      actionneurs: selectedActionneurs,
      timezone
    };
    try {
      if (isEdit) {
        await automatisationsAPI.update(editId, payload);
      } else {
        await automatisationsAPI.create(payload);
      }
      navigate("/automatisations");
    } catch (err) {
      if (err.response?.status === 409) {
        setConflicts(err.response.data.conflicts || []);
        setApiError(isEdit ? "Impossible de modifier : des conflits horaires existent." : "Impossible de créer : des conflits horaires existent.");
      } else {
        setApiError(err.response?.data?.error || (isEdit ? "Erreur lors de la modification" : "Erreur lors de la création"));
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedActData = actionneurs.filter(a => selectedActionneurs.includes(a._id));
  const blockingConflicts = conflicts.filter(c => c.automatisation);
  const warningConflicts = conflicts.filter(c => c.evenement);
  const totalConflicts = blockingConflicts.length + warningConflicts.length;

  // Timeline helpers
  const timelineHours = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24];
  const slotToPercent = (hhmm) => (toMinutes(hhmm) / 1440) * 100;

  // Aperçu langage naturel
  const buildPreview = () => {
    if (creneaux.length === 0 || selectedJours.length === 0 || selectedActData.length === 0) return null;

    const joursText = [...selectedJours].sort((a, b) => a - b).map(i => joursLabels[i]).join(", ");
    const moisText = selectedMois.length > 0 && selectedMois.length < 12
      ? selectedMois.map(m => moisLabels[mois.indexOf(m)]).join(", ")
      : null;

    const actByCreneau = (c) => {
      const ids = (c.actionneurs && c.actionneurs.length > 0) ? c.actionneurs : selectedActionneurs;
      return ids.map(id => {
        const a = actionneurs.find(x => x._id === id);
        return a ? `${a.type} ${a.sn}` : id;
      });
    };

    const creneauxText = creneaux.map(c => {
      const verb = c.action === "Activer" ? t('createAutomation.enable') : t('createAutomation.disable');
      const cibles = actByCreneau(c).join(" et ");
      return `${verb} ${cibles} de ${c.debut} à ${c.fin}`;
    });

    return { joursText, moisText, creneauxText };
  };

  const preview = buildPreview();

  return (
    <>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/automatisations")}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
            <ArrowLeft size={15} />
          </button>
          <div>
            <h2 className="text-base font-bold text-slate-800">{isEdit ? t('createAutomation.editTitle') : t('createAutomation.createTitle')}</h2>
            <p className="text-slate-400 text-[11px]">{t('createAutomation.project')} {projet?.nom || projetId || "—"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-lg text-[11px] font-medium text-blue-700">
            {timezone}
          </span>
          <button onClick={handleSave} disabled={!isValid || saving || blockingConflicts.length > 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              isValid && !saving && blockingConflicts.length === 0
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}>
            <Save size={15} /> {saving ? t('createAutomation.saving') : isEdit ? t('createAutomation.update') : t('createAutomation.save')}
          </button>
        </div>
      </div>

      {/* ════ 2-COLUMN LAYOUT ════ */}
      <div className="flex gap-4 items-start">

        {/* ─── LEFT: Planning ─── */}
        <div className="flex-1 min-w-0 space-y-3">

          {/* Nom + Description */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">{t('createAutomation.nameLabel')}</label>
                <input value={nom} onChange={e => setNom(e.target.value)}
                  placeholder="Ex: Éclairage automatique bureau"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">{t('createAutomation.descriptionLabel')}</label>
                <input value={description} onChange={e => setDescription(e.target.value)}
                  placeholder="Description..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* Jours + Mois (compact, side by side) */}
          <div className="grid grid-cols-2 gap-3">
            {/* Jours */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center">
                    <Calendar size={14} className="text-green-500" />
                  </div>
                  <span className="text-sm font-semibold text-slate-800">{t('createAutomation.activationDays')}</span>
                </div>
                <button onClick={() => setSelectedJours([0,1,2,3,4,5,6])}
                  className="text-[11px] text-blue-600 hover:underline font-medium">
                  {t('createAutomation.allDays')}
                </button>
              </div>
              <div className="flex gap-1.5">
                {jours.map((j, i) => (
                  <button key={i} onClick={() => toggleJour(i)}
                    className={`flex-1 rounded-lg py-2 text-center text-xs font-bold border-2 transition-all ${
                      selectedJours.includes(i)
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200 text-slate-500 hover:border-gray-300"
                    }`}>
                    {j}
                  </button>
                ))}
              </div>
            </div>

            {/* Mois */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Calendar size={14} className="text-purple-500" />
                  </div>
                  <span className="text-sm font-semibold text-slate-800">{t('createAutomation.monthsOptional')}</span>
                </div>
                <button onClick={() => setSelectedMois(mois)}
                  className="text-[11px] text-purple-600 hover:underline font-medium">
                  {t('createAutomation.allMonths')}
                </button>
              </div>
              <div className="grid grid-cols-6 gap-1">
                {mois.map((m, idx) => (
                  <button key={m} onClick={() => toggleMois(m)}
                    className={`rounded-lg py-1.5 text-[11px] font-medium border transition-all ${
                      selectedMois.includes(m)
                        ? "border-purple-500 bg-purple-50 text-purple-700"
                        : "border-gray-200 text-slate-500 hover:border-gray-300"
                    }`}>
                    {moisLabels[idx].slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Créneaux horaires */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock size={14} className="text-blue-500" />
              </div>
              <span className="text-sm font-semibold text-slate-800">{t('createAutomation.timeSlots')}</span>
              <span className="text-[11px] text-slate-400 ml-1">{creneaux.length} créneau(x)</span>
            </div>

            {/* Timeline des plages occupées */}
            {occupiedSlots.length > 0 && (
              <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-[11px] font-semibold text-slate-500 mb-2">{t('createAutomation.occupiedSlots')}</p>
                <div className="relative h-6 bg-white rounded border border-gray-200 overflow-hidden">
                  {occupiedSlots.flatMap((s, i) => {
                    const sStart = slotToPercent(s.debut);
                    const sEnd = slotToPercent(s.fin);
                    const tooltip = `${s.autoNom} : ${s.debut} → ${s.fin} (${s.action}) — ${s.actionneurs.join(', ')}`;
                    const cls = "absolute top-0 h-full bg-red-400/60 border-l border-r border-red-500/40";
                    if (sEnd > sStart) {
                      return [<div key={i} className={cls} style={{ left: `${sStart}%`, width: `${sEnd - sStart}%` }} title={tooltip} />];
                    }
                    // traversée minuit → 2 bandes : debut→24h et 0→fin
                    return [
                      <div key={`${i}-a`} className={cls} style={{ left: `${sStart}%`, width: `${100 - sStart}%` }} title={tooltip} />,
                      <div key={`${i}-b`} className={cls} style={{ left: `0%`, width: `${sEnd}%` }} title={tooltip} />,
                    ];
                  })}
                </div>
                <div className="flex justify-between mt-1">
                  {timelineHours.filter((_, i) => i % 2 === 0).map(h => (
                    <span key={h} className="text-[9px] text-slate-400">{String(h).padStart(2, "0")}h</span>
                  ))}
                </div>
              </div>
            )}

            {/* Add slot row */}
            <div className="flex gap-2 mb-3">
              <input type="time" value={heureDebut} onChange={e => setHeureDebut(e.target.value)}
                className="w-28 px-2.5 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input type="time" value={heureFin} onChange={e => setHeureFin(e.target.value)}
                className="w-28 px-2.5 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <select value={action} onChange={e => setAction(e.target.value)}
                className="w-32 px-2.5 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="Activer">{t('createAutomation.enable')}</option>
                <option value="Désactiver">{t('createAutomation.disable')}</option>
              </select>
              <button
                onClick={addCreneau}
                disabled={selectedActionneurs.length === 0}
                title={selectedActionneurs.length === 0 ? "Sélectionnez au moins un actionneur avant d'ajouter un créneau" : ""}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                  selectedActionneurs.length === 0
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}>
                <Plus size={14} /> {t('createAutomation.addSlot')}
              </button>
            </div>

            {/* Hint passage minuit */}
            {heureDebut > heureFin && heureDebut !== heureFin && (
              <p className="text-[11px] text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-md px-3 py-1.5 mb-3">
                ⓘ Ce créneau traverse minuit : démarrage à <strong>{heureDebut}</strong> le jour sélectionné, arrêt à <strong>{heureFin}</strong> le lendemain.
              </p>
            )}

            {/* Ciblage créneau (si >1 actionneur) */}
            {selectedActData.length > 1 && (
              <div className="mb-3 p-3 bg-gradient-to-r from-orange-50 to-blue-50 rounded-lg border border-orange-200/50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold text-slate-700">Appliquer ce créneau à :</p>
                  {creneauActionneurs.length > 0 && (
                    <button onClick={() => setCreneauActionneurs([])}
                      className="text-[10px] text-slate-400 hover:text-slate-600 underline">Tout désélectionner</button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedActData.map(a => (
                    <button key={a._id}
                      onClick={() => setCreneauActionneurs(prev => prev.includes(a._id) ? prev.filter(x => x !== a._id) : [...prev, a._id])}
                      className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium border-2 transition-all flex items-center gap-1 ${
                        creneauActionneurs.includes(a._id) ? "border-orange-500 bg-orange-500 text-white" : "border-gray-200 text-slate-600 bg-white hover:border-orange-200"
                      }`}>
                      <Zap size={10} /> {a.sn}
                    </button>
                  ))}
                </div>
                {creneauActionneurs.length === 0 && (
                  <p className="text-[10px] text-slate-400 italic mt-2">
                    Aucun sélectionné — le créneau s'appliquera à tous les actionneurs.
                  </p>
                )}
              </div>
            )}

            {/* Liste créneaux */}
            {creneaux.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                <Clock size={24} className="text-gray-300 mx-auto mb-1.5" />
                <p className="text-slate-400 text-xs font-medium">{t('createAutomation.noTimeSlot')}</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {creneaux.map((c, i) => {
                  const cActNames = (c.actionneurs && c.actionneurs.length > 0)
                    ? c.actionneurs.map(id => actionneurs.find(a => a._id === id)?.sn || id.slice(-6))
                    : null;
                  const crossesMidnight = toMinutes(c.fin) <= toMinutes(c.debut);
                  return (
                    <div key={i} className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
                      <span className="text-sm font-bold text-blue-700">{c.debut} → {c.fin}</span>
                      {crossesMidnight && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200"
                          title={`Le créneau démarre le jour sélectionné à ${c.debut} et se termine le lendemain à ${c.fin}`}
                        >
                          +1j
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded text-[11px] font-bold ${c.action === 'Activer' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {c.action === 'Activer' ? 'ON' : 'OFF'}
                      </span>
                      {cActNames && cActNames.map((name, idx) => (
                        <span key={idx} className="text-[10px] bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded font-medium">{name}</span>
                      ))}
                      {!cActNames && selectedActData.length > 1 && <span className="text-[10px] text-slate-400">Tous</span>}
                      <button onClick={() => setCreneaux(prev => prev.filter((_, j) => j !== i))} className="ml-auto">
                        <X size={13} className="text-slate-400 hover:text-red-500" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Aperçu en langage naturel */}
          {preview && (
            <div className="bg-teal-50 border-2 border-teal-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Clock size={14} className="text-teal-600" />
                </div>
                <p className="font-semibold text-slate-800 text-sm">{t('createFunction.preview', 'Aperçu')}</p>
                <span className="text-[10px] bg-teal-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">
                  {t('createFunction.naturalLanguage', 'Langage naturel')}
                </span>
              </div>
              <div className="bg-white rounded-xl p-3 border border-teal-100 space-y-1">
                <p className="text-sm text-slate-700 leading-relaxed">
                  <span className="font-semibold text-teal-700">{t('createFunction.if', 'SI')} </span>
                  <span className="text-blue-600 font-medium">on est le </span>
                  <span className="font-medium">{preview.joursText}</span>
                  {preview.moisText && (
                    <>
                      <span className="text-blue-600 font-medium"> en </span>
                      <span className="font-medium">{preview.moisText}</span>
                    </>
                  )}
                </p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  <span className="font-semibold text-orange-600">{t('createFunction.then', 'ALORS')} </span>
                  <span className="font-medium">{preview.creneauxText.join(", puis ")}</span>
                </p>
              </div>
              <div className="flex gap-2 mt-2">
                <span className="text-[11px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{selectedJours.length} jour{selectedJours.length > 1 ? "s" : ""}</span>
                <span className="text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-md">{selectedActData.length} actionneur{selectedActData.length > 1 ? "s" : ""}</span>
                <span className="text-[11px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-md">{creneaux.length} créneau{creneaux.length > 1 ? "x" : ""}</span>
              </div>
            </div>
          )}

          {/* Conflits */}
          {totalConflicts > 0 && (
            <button onClick={() => setShowConflictModal(true)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium w-full justify-center transition-all ${
                blockingConflicts.length > 0
                  ? "bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
                  : "bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100"
              }`}>
              <AlertTriangle size={14} />
              {blockingConflicts.length > 0
                ? `${blockingConflicts.length} ${t('createAutomation.blockingConflicts')}`
                : `${warningConflicts.length} ${t('createAutomation.warningConflicts')}`}
              {warningConflicts.length > 0 && blockingConflicts.length > 0 && ` + ${warningConflicts.length} avert.`}
              <span className="ml-1 underline">{t('createAutomation.viewDetails')}</span>
            </button>
          )}

          {/* Erreurs */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <AlertCircle size={14} className="text-red-500" />
                <p className="text-xs font-medium text-red-700">{apiError}</p>
              </div>
            </div>
          )}
        </div>

        {/* ─── RIGHT: Sidebar Actionneurs ─── */}
        <div className="w-72 shrink-0 sticky top-4 space-y-3">

          {/* Actionneurs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-orange-50 to-orange-100/50 border-b border-orange-100">
              <div className="flex items-center gap-2">
                <Power size={15} className="text-orange-500" />
                <span className="text-sm font-semibold text-slate-800">{t('createAutomation.actuatorsTitle')}</span>
                <span className="ml-auto text-[11px] font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                  {selectedActData.length}/{actionneurs.length}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">{t('createAutomation.clickToSelect', 'Cliquez pour sélectionner')}</p>
            </div>
            <div className="p-3">
              {actionneurs.length === 0 ? (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                  <Power size={20} className="text-gray-300 mx-auto mb-1" />
                  <p className="text-slate-400 text-[11px] font-medium">{t('createAutomation.noActuatorInProject', 'Aucun actionneur dans le projet')}</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {actionneurs.map(a => {
                    const isSelected = selectedActionneurs.includes(a._id);
                    return (
                      <button
                        key={a._id}
                        type="button"
                        onClick={() => isSelected ? removeActionneur(a._id) : addActionneur(a._id)}
                        className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 border-2 transition-all text-left ${
                          isSelected
                            ? "border-orange-400 bg-orange-50 hover:border-orange-500"
                            : "border-gray-100 bg-white hover:border-orange-200 hover:bg-orange-50/40"
                        }`}
                      >
                        <Zap size={13} className={`shrink-0 ${isSelected ? "text-orange-500" : "text-slate-400"}`} />
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs font-semibold truncate ${isSelected ? "text-slate-800" : "text-slate-600"}`}>{a.sn}</p>
                          <p className="text-[10px] text-slate-400 truncate">{a.type}</p>
                        </div>
                        <div className={`shrink-0 w-4 h-4 rounded-full flex items-center justify-center transition-all ${
                          isSelected ? "bg-orange-500" : "bg-gray-100"
                        }`}>
                          {isSelected
                            ? <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                            : <Plus size={10} className="text-gray-400" />
                          }
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Validation checklist */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <span className="text-sm font-semibold text-slate-800">Validation</span>
            </div>
            <div className="p-3 space-y-1.5">
              {[
                { ok: !!nom, label: t('createAutomation.nameLabel') },
                { ok: selectedActionneurs.length > 0, label: t('createAutomation.actuatorsTitle') },
                { ok: selectedJours.length > 0, label: t('createAutomation.activationDays') },
                { ok: creneaux.length > 0, label: t('createAutomation.timeSlots') },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${item.ok ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {item.ok
                      ? <svg className="w-2.5 h-2.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      : <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                    }
                  </div>
                  <span className={`text-xs ${item.ok ? 'text-slate-600' : 'text-slate-400'}`}>{item.label}</span>
                </div>
              ))}
              {blockingConflicts.length > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center">
                    <X size={10} className="text-red-500" />
                  </div>
                  <span className="text-xs text-red-600 font-medium">{blockingConflicts.length} conflit(s)</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ════ MODALS (unchanged) ════ */}
      <Modal
        isOpen={showConflictModal}
        onClose={() => { setShowConflictModal(false); setOverlapWarning(null); }}
        title={t('createAutomation.conflictsDetected')}
        subtitle={t('createAutomation.conflictsSubtitle')}
        size="md"
      >
        {overlapWarning && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={16} className="text-orange-500" />
              <p className="text-sm font-semibold text-orange-700">{t('createAutomation.overlapWithOccupied')}</p>
            </div>
            <p className="text-xs text-orange-600 mb-2">
              Le créneau <strong>{overlapWarning.debut} → {overlapWarning.fin}</strong> ({overlapWarning.action}) chevauche la plage
              de <strong>{overlapWarning.overlap.autoNom}</strong> ({overlapWarning.overlap.debut} → {overlapWarning.overlap.fin}).
            </p>
            <button
              onClick={() => {
                setCreneaux(prev => sortCreneaux([...prev, { debut: overlapWarning.debut, fin: overlapWarning.fin, action: overlapWarning.action, actionneurs: overlapWarning.actionneurs || [] }]));
                setOverlapWarning(null);
                setShowConflictModal(false);
              }}
              className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-xs font-medium hover:bg-orange-700 transition-colors"
            >
              {t('createAutomation.continueOverlap')}
            </button>
          </div>
        )}

        {blockingConflicts.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={16} className="text-red-500" />
              <p className="text-sm font-semibold text-red-700">{t('createAutomation.blockingTimeConflicts')} ({blockingConflicts.length})</p>
            </div>
            <p className="text-xs text-red-600 mb-3">{t('createAutomation.modifyToResolve')}</p>
            <div className="space-y-2">
              {blockingConflicts.map((c, i) => (
                <div key={i} className="bg-red-50 border border-red-100 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-slate-800">{c.automatisation.nom}</span>
                    <span className="text-xs text-red-500 font-medium">{t('createAutomation.conflict')}</span>
                  </div>
                  <div className="text-xs text-slate-500 space-y-0.5">
                    <p>{t('createAutomation.actuatorsLabel')} {c.actionneurs?.join(", ")}</p>
                    <p>{t('createAutomation.daysLabel')} {c.jours?.join(", ")}</p>
                    <p>{t('createAutomation.existingLabel')} {c.existant?.debut} → {c.existant?.fin} ({c.existant?.action})</p>
                    <p>{t('createAutomation.newLabel')} {c.nouveau?.debut} → {c.nouveau?.fin} ({c.nouveau?.action})</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {warningConflicts.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={16} className="text-amber-500" />
              <p className="text-sm font-semibold text-amber-700">{t('createAutomation.eventConflicts')} ({warningConflicts.length})</p>
            </div>
            <p className="text-xs text-amber-600 mb-3">{t('createAutomation.eventConflictsDesc')}</p>
            <div className="space-y-2">
              {warningConflicts.map((c, i) => (
                <div key={i} className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-slate-800">{c.evenement.nom}</span>
                    <span className="text-xs text-amber-500 font-medium">{t('createAutomation.warning')}</span>
                  </div>
                  <p className="text-xs text-slate-500">{c.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {totalConflicts === 0 && !overlapWarning && (
          <p className="text-sm text-slate-500 text-center py-4">{t('createAutomation.noConflicts')}</p>
        )}

        <div className="flex justify-end pt-2 border-t border-gray-100 mt-4">
          <button
            onClick={() => { setShowConflictModal(false); setOverlapWarning(null); }}
            className="px-4 py-2 bg-gray-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            {t('createAutomation.closeModal')}
          </button>
        </div>
      </Modal>
    </>
  );
}
